/**
 * Log Analyzer Service
 * @description API methods for Log Analyzer-related operations
 */

import { logAnalyzerApi, ApiError } from './api';
import type {
  FrequencyResponse,
  DomainListResponse,
  SeverityDistributionResponse,
  TopErrorsResponse,
  CategoriesResponse,
  RequestTypesResponse,
} from '@/types/logAnalyzer';

/**
 * Log Analyzer Service
 * API methods for log analyzer analytics
 */
export const logAnalyzerService = {
  /**
   * Fetches historical error frequency trend data
   * @param domain - Domain name (e.g., "ALL", "Sites-MX-SharkNinja-Site")
   * @param window - Time window (e.g., "24h", "1h")
   * @param step - Step interval (e.g., "1h", "5m")
   * @returns Frequency response with time series data
   * @throws ApiError if the request fails
   */
  getFrequency: async (domain: string, window: string, step: string): Promise<FrequencyResponse> => {
    try {
      return await logAnalyzerApi.get<FrequencyResponse>(`/domains/${domain}/frequency`, {
        window,
        step,
      });
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch frequency data');
    }
  },

  /**
   * Fetches frequency data using the /v1/domains/ALL/frequency endpoint
   * @param window - Time window (e.g., "1h", "24h")
   * @param step - Step interval (e.g., "5m", "1h")
   * @returns Frequency response with time series data
   * @throws ApiError if the request fails
   */
  getFrequencySimple: async (window: string, step: string, domain?: string): Promise<FrequencyResponse> => {
    try {
      const domainParam = domain || 'ALL';
      return await logAnalyzerApi.get<FrequencyResponse>(`/domains/${domainParam}/frequency`, {
        window,
        step,
      });
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch frequency data');
    }
  },

  /**
   * Fetches domain comparison list
   * @param window - Time window (e.g., "24h", "1h")
   * @returns Domain list response with error counts per domain
   * @throws ApiError if the request fails
   */
  getDomainList: async (window: string, domain?: string): Promise<DomainListResponse> => {
    try {
      const params: { window: string; domain?: string } = { window };
      if (domain) {
        params.domain = domain;
      }
      return await logAnalyzerApi.get<DomainListResponse>('/domains/list', params);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch domain list');
    }
  },

  /**
   * Fetches domain list without time window
   * @param domain - Optional domain filter
   * @returns Domain list response with error counts per domain
   * @throws ApiError if the request fails
   */
  getDomainListWithoutWindow: async (domain?: string): Promise<DomainListResponse> => {
    try {
      const params: { domain?: string } = {};
      if (domain) {
        params.domain = domain;
      }
      return await logAnalyzerApi.get<DomainListResponse>('/domains/list', params);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch domain list');
    }
  },

  /**
   * Fetches severity distribution data
   * @param domain - Domain name (e.g., "ALL", "Sites-MX-SharkNinja-Site")
   * @param window - Time window (e.g., "24h", "1h")
   * @returns Severity distribution response with breakdown by severity level
   * @throws ApiError if the request fails
   */
  getSeverityDistribution: async (domain: string, window: string): Promise<SeverityDistributionResponse> => {
    try {
      return await logAnalyzerApi.get<SeverityDistributionResponse>(`/domains/${domain}/severity`, {
        window,
      });
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch severity distribution');
    }
  },

  /**
   * Fetches top error messages
   * @param domain - Domain name (e.g., "ALL", "Sites-MX-SharkNinja-Site")
   * @param window - Time window (e.g., "24h", "1h", "7d", "15d", "30d") - optional if start_date/end_date provided
   * @param limit - Maximum number of errors to return (default: 20)
   * @param severity - Optional severity filter (e.g., "error", "warn", "fatal")
   * @param start_date - Optional start date (ISO format) - overrides window if provided
   * @param end_date - Optional end date (ISO format) - overrides window if provided
   * @param sort_by - Optional sort parameter (default: "count", options: "count", "latest_error")
   * @returns Top errors response with most frequent error messages
   * @throws ApiError if the request fails
   */
  getTopErrors: async (
    domain: string,
    window?: string,
    limit: number = 20,
    severity?: string,
    start_date?: string,
    end_date?: string,
    sort_by?: string
  ): Promise<TopErrorsResponse> => {
    try {
      const params: Record<string, unknown> = {
        limit,
      };
      
      // Add window if provided and no date range
      if (window && !start_date && !end_date) {
        params.window = window;
      }
      
      // Add date range if provided (overrides window)
      if (start_date) {
        params.start_date = start_date;
      }
      if (end_date) {
        params.end_date = end_date;
      }
      
      // Only add severity if provided and not empty
      if (severity && severity !== '' && severity !== 'all') {
        params.severity = severity;
      }
      
      // Add sort_by if provided
      if (sort_by) {
        params.sort_by = sort_by;
      }
      
      return await logAnalyzerApi.get<TopErrorsResponse>(`/domains/${domain}/top-errors`, params);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch top errors');
    }
  },

  /**
   * Fetches error categories by domain
   * @param domain - Domain name (e.g., "ALL", "Sites-MX-SharkNinja-Site")
   * @param window - Time window (e.g., "24h", "1h")
   * @param severity - Optional severity filter (default: "error")
   * @returns Categories response with category breakdown
   * @throws ApiError if the request fails
   */
  getCategoriesByDomain: async (domain: string, window: string, severity?: string): Promise<CategoriesResponse> => {
    try {
      const params: Record<string, unknown> = {
        window: window || '24h',
      };
      
      // Only add severity parameter if provided and not "all"
      if (severity && severity !== 'all') {
        params.severity = severity;
      }
      
      return await logAnalyzerApi.get<CategoriesResponse>(`/domains/${domain}/by-category`, params);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch categories data');
    }
  },

  /**
   * Fetches request types by domain
   * @param domain - Domain name (e.g., "ALL", "Sites-MX-SharkNinja-Site")
   * @param window - Time window (e.g., "24h", "1h")
   * @param severity - Optional severity filter (default: "error")
   * @returns Request types response with request type breakdown
   * @throws ApiError if the request fails
   */
  getRequestTypesByDomain: async (domain: string, window: string, severity?: string): Promise<RequestTypesResponse> => {
    try {
      const params: Record<string, unknown> = {
        window: window || '24h',
      };
      
      // Only add severity parameter if provided and not "all"
      if (severity && severity !== 'all') {
        params.severity = severity;
      }
      
      return await logAnalyzerApi.get<RequestTypesResponse>(`/domains/${domain}/by-request-type`, params);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch request types data');
    }
  },
};

export default logAnalyzerService;

